/**
 * The whole search feature as one mount: the panel, the find-next bar, and
 * the keyboard shortcut that opens it.
 *
 * Hosts mount this once at app level and render a button that calls
 * `onOpenChange(true)`. Keeping the three pieces together here is what stops
 * the platform and the runtime from each wiring two of them and forgetting
 * the third — the bar in particular is easy to leave out and impossible to
 * notice missing until somebody asks why nothing is highlighted.
 *
 * The bar is hidden while the panel is open (they occupy the same corner of
 * the reader's attention) but NOT cleared: closing the panel without choosing
 * anything brings the previous result's highlight back, which is what a reader
 * who opened search to check something else expects.
 */

import { useEffect } from 'react'
import { GlobalSearchPanel, type GlobalSearchPanelProps } from './GlobalSearchPanel'
import { SearchMatchBar } from './SearchMatchBar'

export interface AppSearchProps extends Omit<GlobalSearchPanelProps, 'onClose'> {
  onOpenChange: (open: boolean) => void
  /** Limit the highlight scan (defaults to the whole document body). */
  highlightRoot?: HTMLElement | null
  /** Set false to leave Cmd/Ctrl+K to the host. */
  shortcut?: boolean
}

export function AppSearch({
  open, onOpenChange, highlightRoot = null, shortcut = true, ...panel
}: AppSearchProps) {
  useEffect(() => {
    if (!shortcut) return undefined
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        onOpenChange(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [shortcut, onOpenChange])

  return (
    <>
      <GlobalSearchPanel {...panel} open={open} onClose={() => onOpenChange(false)} />
      <SearchMatchBar root={highlightRoot} hidden={open} />
    </>
  )
}

export default AppSearch
